import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import { mainCategoryData, hingeOptions, handleOptions } from "../data";
import { saveQuotation, updateQuotation, fetchQuotationById } from "../api";
import "./NewQuotationPage.css";

const getFinancialYear = () => {
  const d = new Date();
  const m = d.getMonth();
  let startYear = d.getFullYear();
  if (m >= 0 && m <= 2) startYear -= 1;
  return startYear;
};

export default function NewQuotationPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const currentFY = getFinancialYear();

  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [items, setItems] = useState([]);

  const [formData, setFormData] = useState({
    documentNo: "",
    date: new Date().toISOString().split("T")[0],
    companyName: "",
    address: "",
    gstin: "",
    contactName: "",
    contactNo: "",
    contactEmail: "",   // ⭐ Added
    productName: "",
    mainCategory: "C3.3",
    hingeType: hingeOptions[0] || "Hinge Small",
    handleType: handleOptions[0] || "Surface Handle - small",
    latchType: "",
    caseColorType: "",
    caseLength: 0,
    caseWidth: 0,
    caseTopHeight: 0,
    caseBottomHeight: 0,
    caseQuantity: 1,
    subtotal: 0,
    grandTotal: 0,
    financialYear: currentFY,
  });


  // ---------- LOAD EXISTING QUOTATION ----------
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const loadQuotation = async () => {
      try {
        const q = await fetchQuotationById(id);
        setFormData({
          documentNo: q.documentNo || "",
          date: q.date || new Date().toISOString().split("T")[0],
          companyName: q.companyName || "",
          address: q.address || "",
          gstin: q.gstin || "",
          contactName: q.contactName || "",
          contactNo: q.contactNo || "",
          contactEmail: q.contactEmail || "",  // ⭐ Added
          productName: q.productName || "",
          mainCategory: q.mainCategory || "C3.3",
          hingeType: q.hingeType || hingeOptions[0] || "Hinge Small",
          handleType: q.handleType || handleOptions[0] || "Surface Handle - small",
          latchType: q.latchType || "",
          caseColorType: q.caseColorType || "Silver",
          caseLength: +q.caseLength || 0,
          caseWidth: +q.caseWidth || 0,
          caseTopHeight: +q.caseTopHeight || 0,
          caseBottomHeight: +q.caseBottomHeight || 0,
          caseQuantity: q.caseQuantity || 1,
          subtotal: q.subtotal || 0,
          grandTotal: q.grandTotal || 0,
          financialYear: q.financialYear || currentFY,
        });

        setMaterials(q.materials || []);
        setItems(q.items || []);
      } catch (err) {
        console.error("Error loading quotation:", err);
      } finally {
        setLoading(false);
      }
    };

    loadQuotation();
  }, [id, currentFY]);

  // ---------- MATERIAL CALCULATION ----------
  useEffect(() => {
    if (loading) return;

    const { caseLength, caseWidth, caseTopHeight, caseBottomHeight, mainCategory } = formData;
    const newMaterials = [];

    if (mainCategory === "C3.3" || mainCategory === "C5.5") {
      const offset = mainCategory === "C3.3" ? -15 : +11;
      const mdfLength = caseLength + offset;
      const mdfWidth = caseWidth + offset;
      const mdfTopHeight = caseTopHeight + (mainCategory === "C3.3" ? -7.5 : 0);
      const mdfBottomHeight = caseBottomHeight + (mainCategory === "C3.3" ? -7.5 : 0);

      // MDF
      newMaterials.push({
        name: "MDF",
        value:
          (2 *
            (mdfLength * mdfWidth +
              mdfLength * mdfTopHeight +
              mdfLength * mdfBottomHeight +
              mdfWidth * mdfTopHeight +
              mdfWidth * mdfBottomHeight)) /
          1_000_000,
        wastage: 30,
        wastageValue: 0,
      });

      // Eva Sheets
      newMaterials.push({
        name: "Eva Sheets",
        value: (caseLength * caseWidth * (caseTopHeight + caseBottomHeight)) / 1_000_000_000,
        wastage: 20,
        wastageValue: 0,
      });

      // L Angle
      const lAngleLength = caseLength - (mainCategory === "C3.3" ? 28 : 30);
      const lAngleWidth = caseWidth - (mainCategory === "C3.3" ? 28 : 30);
      const lAngleTopHeight = caseTopHeight + (mainCategory === "C3.3" ? -8 : 3);
      const lAngleBottomHeight = caseBottomHeight + (mainCategory === "C3.3" ? -8 : 3);

      newMaterials.push({
        name: "Aluminium L Angle",
        value: (4 * (lAngleLength + lAngleWidth + lAngleTopHeight + lAngleBottomHeight)) / 1000,
        wastage: 20,
        wastageValue: 0,
      });

      // Tongue & Groove
      const tongueLength = caseLength - (mainCategory === "C3.3" ? 28 : 27);
      const tongueWidth = caseWidth - (mainCategory === "C3.3" ? 28 : 27);

      newMaterials.push({
        name: "Aluminium Tongue & Groove",
        value: (2 * (tongueLength + tongueWidth)) / 1000,
        wastage: 20,
        wastageValue: 0,
      });
    }

    newMaterials.forEach((m) => {
      m.wastageValue = parseFloat((m.value * (1 + m.wastage / 100)).toFixed(4));
    });

    setMaterials(newMaterials);
  }, [
    loading,
    formData.caseLength,
    formData.caseWidth,
    formData.caseTopHeight,
    formData.caseBottomHeight,
    formData.mainCategory,
  ]);

  // ---------- ITEMS CALCULATION ----------
  useEffect(() => {
    if (loading) return;

    const baseItems = mainCategoryData[formData.mainCategory] || [];
    const newItems = [];
    const labourItems = [];

    const getWastageQuantity = (name) =>
      materials.find((m) => m.name === name)?.wastageValue || 0;

    const map = {
      "Aluminium Profile Round Angle 3.3": "Aluminium L Angle",
      "Aluminium Profile Square Angle 5.5": "Aluminium L Angle",
      "Aluminium Profile Tongue & Groove 3.3": "Aluminium Tongue & Groove",
      "Aluminium Profile Tongue & Groove 5.5": "Aluminium Tongue & Groove",
      "MDF 3.3": "MDF",
      "MDF 5.5": "MDF",
      "Eva Sheets": "Eva Sheets",
    };

    const fixedQuantities = {
      "Corner 3.3": 8,
      "Corner 5.5": 8,
      "Corner Protector 3.3 - Tongue & Groove": 4,
      "Corner Protector 5.5 - Tongue & Groove": 4,
      Hinge: 2,
      Latch: 2,
      Rivet: 80,
      Handle: 1,
      "Rubber Feet": 4,
      "Plastic Feet": 4,
      Labour: 1,
    };

    const getUnit = (name) => {
      if (
        [
          "Aluminium Profile Round Angle 3.3",
          "Aluminium Profile Square Angle 5.5",
          "Aluminium Profile Tongue & Groove 3.3",
          "Aluminium Profile Tongue & Groove 5.5",
          "Anodizing",
        ].includes(name)
      )
        return "m";
      if (["MDF 3.3", "MDF 5.5"].includes(name)) return "m²";
      if (name === "Eva Sheets") return "m³";
      return "Nos";
    };

    baseItems.forEach((item) => {
      if (item.name.toLowerCase().includes("hinge") && item.name !== formData.hingeType) return;
      if (item.name.toLowerCase().includes("handle") && item.name !== formData.handleType) return;
      if (item.name.toLowerCase().includes("latch") && formData.latchType && item.name !== formData.latchType)
        return;

      let qty = 0;
      if (map[item.name]) qty = getWastageQuantity(map[item.name]);
      else {
        for (const [key, fixedQty] of Object.entries(fixedQuantities)) {
          if (item.name.toLowerCase().includes(key.toLowerCase())) qty = fixedQty;
        }
      }
      if (qty === 0) qty = 1;

      const unit = getUnit(item.name);

      const newItem = {
        ...item,
        unit,
        quantity: parseFloat(qty.toFixed(4)),
        price: item.price || 0,
        amount: parseFloat(((qty || 0) * (item.price || 0)).toFixed(2)),
        category: formData.mainCategory,
      };

      if (item.name.toLowerCase().includes("labour")) labourItems.push(newItem);
      else newItems.push(newItem);
    });

    if (formData.caseColorType === "Black") {
      const anodizingQty =
        getWastageQuantity("Aluminium L Angle") + getWastageQuantity("Aluminium Tongue & Groove");
      const anodizingPrice = 17;
      newItems.push({
        category: formData.mainCategory,
        name: "Anodizing",
        unit: "m",
        quantity: parseFloat(anodizingQty.toFixed(4)),
        price: anodizingPrice,
        amount: parseFloat((anodizingQty * anodizingPrice).toFixed(2)),
      });
    }

    setItems([...newItems, ...labourItems]);
  }, [
    loading,
    formData.mainCategory,
    formData.hingeType,
    formData.handleType,
    formData.latchType,
    formData.caseColorType,
    materials,
  ]);

  // ---------- SUBTOTAL & GRAND TOTAL ----------
  useEffect(() => {
    const subtotal = items.reduce((s, i) => s + (i.amount || 0), 0);
    const qty = formData.caseQuantity || 1;
    const total = subtotal * qty;

    setFormData((prev) => ({
      ...prev,
      subtotal: parseFloat(subtotal.toFixed(2)),
      grandTotal: parseFloat(total.toFixed(2)),
    }));
  }, [items, formData.caseQuantity]);

  // ---------- HANDLERS ----------
  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  const handleMaterialWastageChange = (index, e) => {
    const value = parseFloat(e.target.value) || 0;
    const updated = [...materials];
    updated[index].wastage = value;
    updated[index].wastageValue = parseFloat(
      (updated[index].value * (1 + value / 100)).toFixed(4)
    );
    setMaterials(updated);
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      {
        category: formData.mainCategory,
        name: "",
        quantity: 0,
        price: 0,
        amount: 0,
        unit: "Nos",
      },
    ]);
  };

  // ---------- SAVE FUNCTION ----------
  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.productName) {
      alert("Company Name and Product Name are required.");
      return;
    }

    setSaving(true);

    const subtotal = items.reduce((s, i) => s + (i.amount || 0), 0);
    const qty = formData.caseQuantity || 1;
    const finalTotal = subtotal * qty;

    const payload = {
      ...formData,
      subtotal: parseFloat(subtotal.toFixed(2)),
      caseQuantity: qty,
      grandTotal: parseFloat(finalTotal.toFixed(2)),
      materials: materials.map((m) => ({
        name: m.name,
        value: m.value,
        wastage: m.wastage,
        wastageValue: m.wastageValue,
      })),
      items: items.map((it) => ({
        category: it.category,
        name: it.name,
        quantity: it.quantity,
        unit: it.unit,
        price: it.price,
        amount: it.amount,
      })),
    };

    try {
      if (id) await updateQuotation(id, payload);
      else await saveQuotation(payload);
      alert(`Quotation ${id ? "updated" : "saved"} successfully!`);
      navigate("/list-quotation");
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving quotation: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Header />
      <div className="new-quotation-container">
        <h1>{id ? "Edit Quotation" : "New Quotation"}</h1>

        <form onSubmit={handleSave}>
          {/* Top Info */}
          <div className="quotation-info single-row">
            <div className="info-item">
              <label>Quotation No:</label>
              <input type="text" value={formData.quotationNo} readOnly />
            </div>
            <div className="info-item">
              <label>Financial Year:</label>
              <input type="number" value={formData.financialYear} readOnly />
            </div>
            <div className="info-item">
              <label>Date:</label>
              <input type="text" value={formData.date} readOnly />
            </div>
          </div>

          {/* Customer Details Section */}
          <div className="customer-details">
            <h3>Customer Details</h3>
            <div className="quotation-info">
              <div className="info-item">
                <label>Company Name:</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                />
              </div>

              <div className="info-item">
                <label>Product Name:</label>
                <input
                  type="text"
                  value={formData.productName}
                  onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                />
              </div>

              <div className="info-item">
                <label>GSTIN:</label>
                <input
                  type="text"
                  value={formData.gstin}
                  maxLength={15}
                  onChange={(e) => {
                    const gst = e.target.value.toUpperCase().slice(0, 15);
                    setFormData({ ...formData, gstin: gst });
                  }}
                />
              </div>

              <div className="info-item">
                <label>Contact Name:</label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                />
              </div>

              <div className="info-item">
                <label>Contact No:</label>
                <input
                  type="text"
                  value={formData.contactNo}
                  onChange={(e) => setFormData({ ...formData, contactNo: e.target.value })}
                />
              </div>

              <div className="info-item">
                <label>Contact Email:</label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, contactEmail: e.target.value })
                  }
                />
              </div>


              <div className="info-item">
                <label>Address:</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Dropdowns */}
          <div className="quotation-info single-row">
            <div className="info-item">
              <label>Main Category:</label>
              <select
                value={formData.mainCategory}
                onChange={(e) => setFormData({ ...formData, mainCategory: e.target.value })}
              >
                <option value="C3.3">3.3</option>
                <option value="C5.5">5.5</option>
              </select>
            </div>

            <div className="info-item">
              <label>Hinge Type:</label>
              <select
                value={formData.hingeType}
                onChange={(e) => setFormData({ ...formData, hingeType: e.target.value })}
              >
                {hingeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="info-item">
              <label>Handle Type:</label>
              <select
                value={formData.handleType}
                onChange={(e) => setFormData({ ...formData, handleType: e.target.value })}
              >
                {handleOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="info-item">
              <label>Latch Type:</label>
              <select
                value={formData.latchType || ""}
                onChange={(e) => setFormData({ ...formData, latchType: e.target.value })}
              >
                <option value="">--Select Latch--</option>
                {mainCategoryData[formData.mainCategory]
                  .filter((item) => item.name.toLowerCase().includes("latch"))
                  .map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="info-item">
              <label>Case Color:</label>
              <select
                name="caseColorType"
                value={formData.caseColorType}
                onChange={(e) => setFormData({ ...formData, caseColorType: e.target.value })}
              >
                <option value="">Select Color</option>
                <option value="Silver">Silver</option>
                <option value="Black">Black</option>
              </select>
            </div>
          </div>

          {/* Dimensions */}
          <div className="case-dimensions">
            <h3>Case Dimensions (mm)</h3>
            <div className="dimension-inputs">
              {["caseLength", "caseWidth", "caseTopHeight", "caseBottomHeight"].map((key) => (
                <div className="dimension-item" key={key}>
                  <label>{key.replace(/case/, "").replace(/([A-Z])/g, " $1")}: </label>
                  <input type="number" name={key} value={formData[key]} onChange={handleDimensionChange} />
                </div>
              ))}
            </div>
          </div>

          {/* Materials Table */}
          <table className="material-table vertical-only">
            <thead>
              <tr>
                <th>Material</th>
                <th>Value</th>
                <th>Wastage %</th>
                <th>With Wastage</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m, i) => {
                let unit = "";
                if (m.name === "MDF") unit = "m²";
                else if (m.name === "Eva Sheets") unit = "m³";
                else if (m.name === "Aluminium L Angle" || m.name === "Aluminium Tongue & Groove") unit = "m";

                return (
                  <tr key={i}>
                    <td>{m.name}</td>
                    <td>
                      {m.value?.toFixed(4)} {unit}
                    </td>
                    <td>
                      <input type="number" value={m.wastage} onChange={(e) => handleMaterialWastageChange(i, e)} />
                    </td>
                    <td>
                      {(m.wastageValue ?? 0).toFixed(4)} {unit}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Items Table */}
          <table className="item-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td>
                    <input
                      className="item-input"
                      value={it.name}
                      placeholder="Enter item name"
                      onChange={(e) => {
                        const updated = [...items];
                        updated[i].name = e.target.value;
                        setItems(updated);
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={it.quantity}
                      onChange={(e) => {
                        const updated = [...items];
                        const val = parseFloat(e.target.value) || 0;
                        updated[i].quantity = val;
                        updated[i].amount = parseFloat((val * updated[i].price).toFixed(2));
                        setItems(updated);
                      }}
                    />
                  </td>
                  <td>{it.unit || "Nos"}</td>
                  <td>
                    <input
                      type="number"
                      value={it.price}
                      onChange={(e) => {
                        const updated = [...items];
                        const val = parseFloat(e.target.value) || 0;
                        updated[i].price = val;
                        updated[i].amount = parseFloat((val * updated[i].quantity).toFixed(2));
                        setItems(updated);
                      }}
                    />
                  </td>
                  <td>₹{(it.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button type="button" className="add-row-btn" onClick={handleAddItem}>
            ➕ Add Row
          </button>

          {/* Summary Box */}
          <div className="summary-below">
            <h3>Summary</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td>Per Unit Price</td>
                  <td style={{ textAlign: "right" }}>₹{(formData.subtotal || 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td>Quantity</td>
                  <td style={{ textAlign: "right" }}>
                    <input
                      type="number"
                      min="1"
                      value={formData.caseQuantity || 1}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          caseQuantity: parseFloat(e.target.value) || 1,
                        }))
                      }
                      style={{
                        width: "80px",
                        textAlign: "right",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "3px",
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Taxable Amount</strong>
                  </td>
                  <td style={{ textAlign: "right", fontWeight: "bold" }}>
                    ₹{(formData.grandTotal || 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <button type="submit" disabled={saving}>
            {saving ? "Saving..." : id ? "Update Quotation" : "Save Quotation"}
          </button>
        </form>
      </div>
    </>
  );
}
